import { useEffect, useState, useCallback, Fragment } from "react";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";
import { Loader2, AlertTriangle, Bug, Activity, RefreshCw, ExternalLink } from "lucide-react";

const KIND_META = {
  window_error:        { label: "JS error",       icon: AlertTriangle, cls: "bg-red-100 text-red-800 border-red-200" },
  unhandled_rejection: { label: "Promise reject", icon: Bug,           cls: "bg-amber-100 text-amber-800 border-amber-200" },
  manual:              { label: "Manual",         icon: Activity,      cls: "bg-sky-100 text-sky-800 border-sky-200" },
};

function KindPill({ kind }) {
  const m = KIND_META[kind] || KIND_META.manual;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

function shortPath(url) {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || "");
  } catch (_e) { return url || "—"; }
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)  return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

export default function AdminClientErrors() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pathFilter, setPathFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "150" });
      if (pathFilter) params.set("path_contains", pathFilter);
      if (kindFilter) params.set("kind", kindFilter);
      const { data } = await api.get(`/admin/client-errors?${params.toString()}`);
      setErrors(data.errors || []);
    } finally { setLoading(false); }
  }, [pathFilter, kindFilter]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  return (
    <AdminShell current="/admin/client-errors">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-neutral-100" data-testid="admin-ce-title">Client errors</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Browser-side errors captured by the lightweight reporter. Capped at 10k entries (5 MB) — oldest auto-evict.
          </p>
        </div>
        <button
          onClick={fetchErrors}
          data-testid="admin-ce-refresh"
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs font-semibold text-neutral-200 hover:bg-neutral-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          value={pathFilter}
          onChange={e => setPathFilter(e.target.value)}
          placeholder="Filter by path (e.g. /onboarding)"
          data-testid="admin-ce-filter-path"
          className="w-64 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-100 placeholder:text-neutral-500"
        />
        <select
          value={kindFilter}
          onChange={e => setKindFilter(e.target.value)}
          data-testid="admin-ce-filter-kind"
          className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-100"
        >
          <option value="">All kinds</option>
          <option value="window_error">JS error</option>
          <option value="unhandled_rejection">Promise reject</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900">
        {loading && errors.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : errors.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-neutral-500" data-testid="admin-ce-empty">
            No client errors logged. Healthy! 🎉
          </div>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="bg-neutral-950/60 text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Kind</th>
                <th className="px-4 py-3 font-semibold">Message</th>
                <th className="px-4 py-3 font-semibold">Path</th>
                <th className="px-4 py-3 font-semibold">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {errors.map((e, i) => {
                const key = `${e.ts}_${i}`;
                const isOpen = expanded === key;
                return (
                  <Fragment key={key}>
                    <tr
                      data-testid={`admin-ce-row-${i}`}
                      onClick={() => setExpanded(isOpen ? null : key)}
                      className="cursor-pointer hover:bg-neutral-800/50"
                    >
                      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{timeAgo(e.ts)}</td>
                      <td className="px-4 py-3"><KindPill kind={e.kind} /></td>
                      <td className="px-4 py-3 font-mono text-neutral-100">{e.message}</td>
                      <td className="px-4 py-3 text-neutral-400 font-mono">{shortPath(e.url)}</td>
                      <td className="px-4 py-3 text-neutral-400">{e.user_id ? `${e.user_role || "?"} · ${e.user_id.slice(0, 12)}` : "anon"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-neutral-950">
                        <td colSpan={5} className="px-4 py-4">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Detail label="Full URL" value={e.url} link />
                            <Detail label="IP" value={e.ip} />
                            <Detail label="User agent" value={e.user_agent} />
                            <Detail label="Viewport" value={e.viewport} />
                          </div>
                          {e.stack && (
                            <div className="mt-3">
                              <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">Stack</div>
                              <pre className="max-h-64 overflow-auto rounded-md border border-neutral-800 bg-neutral-950 p-3 text-[11px] text-neutral-300 whitespace-pre-wrap">{e.stack}</pre>
                            </div>
                          )}
                          {e.extra && Object.keys(e.extra).length > 0 && (
                            <div className="mt-3">
                              <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">Extra</div>
                              <pre className="rounded-md border border-neutral-800 bg-neutral-950 p-3 text-[11px] text-neutral-300">{JSON.stringify(e.extra, null, 2)}</pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}

function Detail({ label, value, link }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-[11px] text-sky-400 hover:underline">
          {value} <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <div className="break-all text-[11px] text-neutral-300">{value}</div>
      )}
    </div>
  );
}
